import moment from 'moment';
import { Effect, Reducer } from 'umi';
import {
  NodeSelectItem,
  ModelDeleteEdge,
  WorkflowConfig,
  TableData,
} from '@/components/index';
import { getMiniProgram, getLink, getChannel } from '@/services/workflow';
import { MiniProgramType } from '@/pages/miniProgram/types';
import { LinkType } from '@/pages/link/types';
import { Edge } from 'react-flow-renderer';
import { ChannelPayload } from '@/pages/channel/types';

export interface OpenState {
  name: string;
  nodeSelectList: NodeSelectItem[];
  deleteEdge: ModelDeleteEdge[];
  edges: Edge[];
  createNodeInfo: {
    key: string;
    type: 'default' | 'input' | 'output';
    condition?: string;
    source: string;
  };
  workflowConfig: null | WorkflowConfig;
  tableData: TableData;
  miniProgramList: MiniProgramType[];
  channelList: ChannelPayload[];
  linkList: LinkType[];
  actionInfo: {
    show: boolean;
  };
  nodeHidden: {
    nodeId: string;
    hidden: boolean;
  };
  nodeHiddenMap: { [key: string]: boolean[] },
  copyItem: { isCopy: boolean, data: any },
  regexList: { value: string }[],
  selectedNode: string;
  selectedEdge: string;
  conditionEdit: {
    show: boolean;
    nodeId: string;
    condition: string;
    isConfirm: boolean;
    isEdit: false;
  }
}

export interface OpenModelType {
  namespace: 'open';
  state: OpenState;
  effects: {
    addNode: Effect;
    setNodeSelectList: Effect;
    setDeleteEdgeTarget: Effect;
    initState: Effect;
    initWorkflowConfig: Effect;
    sendTableDataChange: Effect;
    apiGetMiniProgramConfigList: Effect;
    apiGetChannelList: Effect;
    apiGetLinkConfigList: Effect;
    showActionEditDrawer: Effect;
    closeActionEditDrawer: Effect;
    handleNodeHidden: Effect;
    initNodeHidden: Effect;
    addNodeHidden: Effect;
    handleEdgesChange: Effect;
    handleCopyItem: Effect;
    setRegexList: Effect;
    handleSelected: Effect;
    handleConditionEdit: Effect;
  };
  reducers: {
    save: Reducer<OpenState>;
    saveNodeMap: Reducer<any>;
    addNodeMap: Reducer<any>;
  };
}

const Open: OpenModelType = {
  namespace: 'open',
  state: {
    name: 'hello world',
    deleteEdge: [],
    edges: [],
    nodeSelectList: [],
    createNodeInfo: {
      key: '',
      type: 'input',
      condition: '是',
      source: '',
    },
    workflowConfig: null,
    tableData: {},
    miniProgramList: [],
    channelList: [],
    linkList: [],
    actionInfo: {
      show: false,
    },
    nodeHidden: {
      nodeId: '',
      hidden: false,
    },
    nodeHiddenMap: {},
    copyItem: { isCopy: false, data: {} },
    regexList: [],
    selectedNode: '',
    selectedEdge: '',
    conditionEdit: {
      show: false,
      isConfirm: false,
      nodeId: '',
      condition: '',
      isEdit: false,
    },
  },
  effects: {
    *handleConditionEdit({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          conditionEdit: payload
        }
      })
    },
    *handleSelected({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload,
      });
    },
    *setRegexList({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          regexList: payload,
        },
      });
    },
    *handleCopyItem({ payload }, { call, put }) {
      yield put({ type: 'save', payload: { copyItem: { isCopy: true, data: payload } } });
      yield put({ type: 'save', payload: { copyItem: { isCopy: false, data: {} } } });
    },
    *apiGetMiniProgramConfigList({ payload }, { call, put }) {
      const { resp } = yield call(getMiniProgram, {});
      if (resp.code !== 0) return;
      yield put({ type: 'save', payload: { miniProgramList: resp.data.list } });
    },
    *apiGetChannelList({ payload }, { call, put }) {
      const { resp } = yield call(getChannel, {});
      if (resp.code !== 0) return;
      yield put({ type: 'save', payload: { channelList: resp.data.list } });
    },
    *apiGetLinkConfigList({ payload }, { call, put }) {
      const { resp } = yield call(getLink, {});
      if (resp.code !== 0) return;
      yield put({ type: 'save', payload: { linkList: resp.data.list } });
    },
    *addNode({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          createNodeInfo: payload,
        },
      });
    },
    *setNodeSelectList({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          nodeSelectList: payload.data,
        },
      });
    },
    *setDeleteEdgeTarget({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          deleteEdge: payload,
        },
      });
    },
    *initState({}, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          createNodeInfo: {
            key: '',
            type: 'input',
            condition: '',
            source: '',
          },
          deleteEdge: [],
        },
      });
    },
    *initWorkflowConfig({ payload }, { call, put }) {
      yield put({ type: 'save', payload: { workflowConfig: payload } });
    },
    *sendTableDataChange({ payload }, { call, put }) {
      yield put({ type: 'save', payload: { tableData: payload } });
    },
    *showActionEditDrawer({ payload }, { call, put }) { // 编辑
      yield put({
        type: 'save',
        payload: {
          actionInfo: { 
            show: true,
            kind: payload.kind,
            d: payload.d,
            nodeId: payload.nodeId, // 不存在则表示为全局规则
          }
        },
      });
    },
    *closeActionEditDrawer({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          actionInfo: { show: false }
        },
      });
    },
    *handleEdgesChange({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload
      })
    },
    *addNodeHidden({ payload }, { call, put }) {
      yield put({
        type: 'addNodeMap',
        payload
      })
    },
    *handleNodeHidden({ payload }, { call, put }) {
      yield put({
        type: 'saveNodeMap',
        payload: {
          key: payload.nodeId,
          value: payload.hidden
        }
      })
      yield put({
        type: 'save',
        payload: {
          nodeHidden: { 
            hidden: payload.hidden,
            nodeId: payload.nodeId
          }
        },
      });
    },
    *initNodeHidden({ payload }, { call, put }) {
      yield put({
        type: 'save',
        payload: {
          nodeHiddenMap: payload
        }
      })
    },
  },
  reducers: {
    save(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
    saveNodeMap(state, { payload }) {
      const _o = state?.nodeHiddenMap;
      const _value = _o[payload.key];
      const _value_length = _value.length;
      const _value_last = _value[_value_length - 1];
      if (_value_length === 1) {
        if (_value_last !== payload.value) _value.push(payload.value);
      } else if (_value_last !== payload.value) {
        _value.pop();
      }
      const _nodeHiddenMap = { ..._o, [payload.key]: _value };
      return {
        ...state,
        nodeHiddenMap: _nodeHiddenMap
      }
    },
    addNodeMap(state, { payload }) {
      return {
        ...state,
        nodeHiddenMap: { ...state.nodeHiddenMap, [payload.key]: payload.value }
      }
    }
  },
};

export default Open;
